import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withFieldProps } from 'hooks/useFieldProps';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import { withStyles } from 'theme/withStyles';
import dialogActions from 'core/dialogs/actions';
import checkboxStyles from 'styles/checkbox';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import buttonLinkStyles from 'styles/buttonLink';

import rootStyles from './styles/root';

const styles = (theme) => {
  const { palette, spacing, typography } = theme;
  return {
    buttonLink: {
      ...buttonLinkStyles(theme),
    },
    description: {
      ...typography.p,
      ...typography.p3,
      '& *': {
        display: 'inline',
        margin: 0,
        padding: 0,
      },
      lineHeight: '1.3em',
      verticalAlign: 'middle',
    },
    input: {
      ...checkboxStyles(theme),
      left: 0,
      position: 'absolute',
      top: 0,
    },
    label: {
      ...typography.h3,
      '& *': {
        display: 'inline',
        margin: 0,
        padding: 0,
      },
      color: ({ error }) => (error ? palette.error : typography.h3.color),
      display: 'block',
      lineHeight: '35px',
      marginBottom: spacing.unit,
    },
    root: {
      ...rootStyles({ spacing }),
      '& fieldset': {
        paddingLeft: spacing.sm + spacing.unit,
        position: 'relative',
      },
    },
  };
};

const MultiDisclosure = ({
  classes,
  error,
  fieldDescr,
  fieldDomId,
  fieldOptionalLabelSuffix,
  help,
  id,
  label,
  loading,
  multipleDisclosures,
  name,
  onChange,
  openDialog,
  readOnly,
  required,
  tooltip,
  validate,
  value,
}) => {
  const optionalLabel = !required
    ? `<small> ${fieldOptionalLabelSuffix || '(Optional)'}</small>`
    : '';
  const labelString = `${label}${optionalLabel}`;
  const focusRef = useRef(null);
  const clickLink = useCallback(
    (e) => {
      e.preventDefault();
      const { dataset } = e.target;
      const { differentiator } = dataset;
      openDialog({
        differentiator,
        onClose: () => focusRef?.current?.focus(),
      });
    },
    [openDialog],
  );
  useEffect(() => {
    const links = document.querySelectorAll('.disclosure-link');
    links.forEach((el) => {
      el.addEventListener('click', clickLink);
    });
    return () =>
      links.forEach((el) => {
        el.removeEventListener('click', clickLink);
      });
  }, [clickLink, openDialog]);

  let mdIndex = 0;
  const fieldDescription =
    multipleDisclosures &&
    fieldDescr &&
    fieldDescr.split('<a href="#">').map((str, index) => {
      if (index === 0) {
        return str;
      }
      if (multipleDisclosures[mdIndex]) {
        const { differentiator, label: discLabel } = multipleDisclosures[mdIndex];
        mdIndex += 1;
        return ''
          .concat(
            `<button
              type="button"
              data-id="${id}"
              data-differentiator="${differentiator}"
              class="buttonLink disclosure-link ${classes.buttonLink}"
            >
              ${discLabel}
            </button>`,
          )
          .concat(str);
      }
      return str;
    });
  return (
    <div className={classnames('form-group', { error }, classes.root)}>
      <div className="border-bottom">
        <RawHtml type="label" htmlFor={fieldDomId} className={classes.label}>
          {labelString}
        </RawHtml>
        <Spinner loading={loading} />
        <div className={classnames('checkbox', 'form-group', { error })}>
          <fieldset>
            <input
              ref={focusRef}
              type="checkbox"
              id={fieldDomId}
              name={fieldDomId}
              checked={value || false}
              aria-describedby={
                classnames({
                  [`help-${name}`]: help,
                  [`error-${name}`]: error,
                }) || undefined
              }
              onChange={() => {
                /* to help trigger the validation error */
                onChange(name, value && required ? '' : !value);
                validate();
              }}
              disabled={readOnly || !required}
              className={classes.input}
            />{' '}
            <RawHtml type="label" htmlFor={fieldDomId} className={classes.description}>
              {fieldDescription && fieldDescription.join('')}
            </RawHtml>
          </fieldset>
          <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
          <Help help={help} id={`help-${name}`} />
        </div>

        <ErrorMsg name={name} />
      </div>
    </div>
  );
};

MultiDisclosure.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDescr: PropTypes.string.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  fieldOptionalLabelSuffix: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  multipleDisclosures: PropTypes.arrayOf(
    PropTypes.shape({
      differentiator: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.bool.isRequired,
};

MultiDisclosure.defaultProps = {
  multipleDisclosures: null,
};

const MultiDisclosureStyled = withStyles(styles)(MultiDisclosure);

const mapStateToProps = ({ data, resources }, { name }) => ({
  fieldOptionalLabelSuffix: resources.fieldOptionalLabelSuffix,
  multipleDisclosures: data.multipleDisclosures?.[name],
});

const mapDispatchToProps = (dispatch, { id }) => ({
  openDialog: (params) => {
    dispatch(dialogActions.open('disclosure', { ...params, id }));
  },
});

export default withFieldProps(mapStateToProps, mapDispatchToProps)(MultiDisclosureStyled);
